import api from '@/services/api'
import { usePlayer } from '@/composables/game/player'
import { useAuth } from '@/composables/auth'
import webapp from '@/services/webapp'
import type { CardChallengeLeaderboardPlayer, CardChallengeType } from '@/types/leaderboard-player'
import { computed, onBeforeMount, ref } from 'vue'
import { useNow } from '@/composables/game/time'
import { DateTime } from 'luxon'
import { useLocalStorage } from '@vueuse/core'
import { isTodayDate } from '@/helpers/date'

export const useLeaderBoard = () => {
  const { player } = usePlayer()
  const { profile } = useAuth()

  async function getLeaderboard(level: number) {
    const leaderboard = await api.getLeaderboard(level)
    if (player.value.level !== level) {
      leaderboard.thisPlayer = {
        rank: null,
        isInList: false,
        isInThisLevel: false,
      }
      leaderboard.players = leaderboard.players.filter(p => !p.isThisPlayer)
      return leaderboard
    }

    if (leaderboard.players.length === leaderboard.maxListLength && leaderboard.players.every(p => p.profit >= player.value.profit)) {
      return leaderboard
    }

    leaderboard.players = leaderboard.players.filter(p => !p.isThisPlayer)

    const index = leaderboard.players.findIndex(p => p.profit < player.value.profit)
    if (index === -1) {
      leaderboard.players.push({
        profit: player.value.profit,
        isThisPlayer: true,
        firstName: profile.value.firstName,
        photoUrl: profile.value.photoUrl,
        isPremium: webapp.isPremium,
      })
      leaderboard.thisPlayer = {
        isInList: true,
        rank: leaderboard.players.length,
        isInThisLevel: true,
      }
    } else {
      leaderboard.players.splice(index, 0, {
        profit: player.value.profit,
        isThisPlayer: true,
        firstName: profile.value.firstName,
        photoUrl: profile.value.photoUrl,
        isPremium: webapp.isPremium,
      })
      leaderboard.thisPlayer = {
        isInList: true,
        rank: index + 1,
        isInThisLevel: true,
      }
    }

    return leaderboard
  }

  return { getLeaderboard }
}

export const useEffortLeaderBoard = () => {
  async function getEffortLeaderboard() {
    return await api.getEffortLeaderboard()
  }

  return { getEffortLeaderboard }
}

export const useCardChallengeLeaderboard = () => {
  const { now, nowDateIso } = useNow()

  const cardChallengeModalTimestamp = useLocalStorage<string>('cardChallengeModalTimestamp', '')

  const canShowDailyChallengeModal = computed(() => {
    if (cardChallengeModalTimestamp.value) {
      return !isTodayDate(DateTime.fromISO(cardChallengeModalTimestamp.value))
    }
    return true
  })

  const cardChallengeLeaderboard = ref<CardChallengeLeaderboardPlayer[]>([])
  const currentPlayer = ref<null | { rank: number, score: number }>(null)

  const remainingTimeFromDay = computed(() => {
    const timeDiff = DateTime.utc().plus({ days: 1 }).startOf('day').toSeconds() - now.value
    if (timeDiff < 0) {
      return 0
    }
    return timeDiff
  })

  const remainingTimeFromWeek = computed(() => {
    const timeDiff = DateTime.utc().plus({ weeks: 1 }).startOf('week').toSeconds() - now.value
    if (timeDiff < 0) {
      return 0
    }
    return timeDiff
  })

  function setCardChallengeModalTimestamp() {
    cardChallengeModalTimestamp.value = nowDateIso.value
  }

  async function getCardChallengeLeaderboard(type: CardChallengeType) {
    try {
      const response = await api.getCardUpgradeLeaderboard(type)
      currentPlayer.value = response.thisPlayer
      cardChallengeLeaderboard.value = response.players
    } catch (e) {
      cardChallengeLeaderboard.value = []
      currentPlayer.value = null
      console.error(e)
    }

  }

  return {
    getCardChallengeLeaderboard,
    cardChallengeLeaderboard,
    currentPlayer,
    remainingTimeFromDay,
    remainingTimeFromWeek,
    canShowDailyChallengeModal,
    setCardChallengeModalTimestamp,
  }
}

export const useCurrentLevelRankFetcher = () => {
  const isFetching = ref(false)
  const isFetched = ref(false)
  const currentRank = ref<number>(0)
  const effortLeaderboardCurrentRank = ref<number>(0)

  async function fetchCurrentRank() {
    console.log('fetch current rank')
    isFetching.value = true
    try {
      const { rank, effortLeaderboardRank } = await api.getCurrentLevelRank()
      currentRank.value = rank ?? 0
      effortLeaderboardCurrentRank.value = effortLeaderboardRank ?? 0
      isFetched.value = true
    } catch (e) {
      currentRank.value = 0
      effortLeaderboardCurrentRank.value = 0
    } finally {
      isFetching.value = false
    }
  }

  onBeforeMount(async () => {
    await fetchCurrentRank()
  })

  return {
    isFetching,
    fetchCurrentRank,
    isFetched,
    currentRank,
    effortLeaderboardCurrentRank,
  }
}

export const useCollectibleChallengeLeaderboard = () => {
  const collectibleChallengeLeaderboard = ref<CardChallengeLeaderboardPlayer[]>([])
  const currentPlayer = ref<null | { rank: number, score: number }>(null)

  async function getCollectibleChallengeLeaderboard() {
    try {
      const response = await api.getSpecialCollectibleChallengeLeaderboard()
      currentPlayer.value = response.thisPlayer
      collectibleChallengeLeaderboard.value = response.players
    } catch (e) {
      collectibleChallengeLeaderboard.value = []
      currentPlayer.value = null
      console.error(e)
    }

  }

  return {
    getCollectibleChallengeLeaderboard,
    collectibleChallengeLeaderboard,
    currentPlayer,
  }
}

export const usePathwayChallengeLeaderboard = () => {
  const pathwayChallengeLeaderboard = ref<CardChallengeLeaderboardPlayer[]>([])
  const currentPlayer = ref<null | { rank: number, score: number }>(null)

  async function getPathwayChallengeLeaderboard() {
    try {
      const response = await api.getPathwayLeaderboard()
      currentPlayer.value = response.thisPlayer
      pathwayChallengeLeaderboard.value = response.players
    } catch (e) {
      pathwayChallengeLeaderboard.value = []
      currentPlayer.value = null
      console.error(e)
    }

  }

  return {
    getPathwayChallengeLeaderboard,
    pathwayChallengeLeaderboard,
    currentPlayer,
  }
}

export const usePuzzleChallengeLeaderboard = () => {
  const puzzleChallengeLeaderboard = ref<CardChallengeLeaderboardPlayer[]>([])
  const currentPlayer = ref<null | { rank: number, score: number }>(null)

  async function getPuzzleChallengeLeaderboard() {
    try {
      const response = await api.getPuzzleChallengeLeaderboard()
      currentPlayer.value = response.thisPlayer
      puzzleChallengeLeaderboard.value = response.players
    } catch (e) {
      puzzleChallengeLeaderboard.value = []
      currentPlayer.value = null
      console.error(e)
    }

  }

  return {
    getPuzzleChallengeLeaderboard,
    puzzleChallengeLeaderboard,
    currentPlayer,
  }
}

export const useMemoryChallengeLeaderboard = () => {
  const memoryChallengeLeaderboard = ref<CardChallengeLeaderboardPlayer[]>([])
  const currentPlayer = ref<null | { rank: number, score: number }>(null)

  async function getMemoryChallengeLeaderboard() {
    try {
      const response = await api.getMemoryGameChallengeLeaderboard()
      currentPlayer.value = response.thisPlayer
      memoryChallengeLeaderboard.value = response.players
    } catch (e) {
      memoryChallengeLeaderboard.value = []
      currentPlayer.value = null
      console.error(e)
    }
  }

  return {
    getMemoryChallengeLeaderboard,
    memoryChallengeLeaderboard,
    currentPlayer,
  }
}

export const useMinesweeperChallengeLeaderboard = () => {
  const minesweeperChallengeLeaderboard = ref<CardChallengeLeaderboardPlayer[]>([])
  const currentPlayer = ref<null | { rank: number, score: number }>(null)

  async function getMinesweeperChallengeLeaderboard() {
    try {
      const response = await api.getMinesweeperGameChallengeLeaderboard()
      currentPlayer.value = response.thisPlayer
      minesweeperChallengeLeaderboard.value = response.players
    } catch (e) {
      minesweeperChallengeLeaderboard.value = []
      currentPlayer.value = null
      console.error(e)
    }
  }

  return {
    getMinesweeperChallengeLeaderboard,
    minesweeperChallengeLeaderboard,
    currentPlayer,
  }
}

